// The rail: which company, what you can find, where you can go, and who you are.
//
// Four regions in that order, and the order is the argument. The reference interfaces
// this is drawn from all put the workspace at the top and the account at the bottom,
// with search directly under the workspace — because "which tenant am I in" changes
// what every number below it means, and "find me a thing" is the shortest path to any
// of them.
//
// riksu had none of that: the tenant sat in the page header as a raw identifier, there
// was no search anywhere in the product, and signing out floated beside the company
// name. This is that corrected.
//
// # The company is a BUTTON, not a label
//
// It is a button even though riksu has exactly one tenant per session and nothing to
// switch to. A caller's token carries one tenant claim and switching means a new
// token, so the control opens a panel that says which company this is and what that
// means, rather than pretending at a switcher. The affordance is honest about being a
// dead end, which is better than a label somebody keeps clicking.

import { BuildingIcon, LogOutIcon, SearchIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { clearWorkingDrafts } from "../lib/working-draft";
import { popoverMotion } from "./motion";

interface RailProps {
  readonly tenant: string;
  readonly who: string;
  readonly search: ReactNode;
  readonly nav: ReactNode;
  readonly signOutLabel: string;
  readonly companyLabel: string;
  readonly oneTenantNote: string;
}

export function Rail({
  tenant,
  who,
  search,
  nav,
  signOutLabel,
  companyLabel,
  oneTenantNote,
}: RailProps) {
  return (
    <div className="flex h-full flex-col gap-4">
      <Popover>
        <PopoverTrigger
          render={
            <Button
              variant="ghost"
              className="h-auto w-full justify-start gap-2 px-2 py-2 text-left"
            />
          }
        >
          {/* A mark rather than an initial: riksu holds a company's legal name, not a
              brand, and a letter in a coloured square is a logo it does not have. */}
          <span className="flex size-7 shrink-0 items-center justify-center rounded-lg border border-border bg-muted">
            <BuildingIcon className="size-3.5 text-muted-foreground" />
          </span>
          <span className="flex min-w-0 flex-col">
            <span className="truncate text-shell-body font-medium">{tenant}</span>
            <span className="truncate text-shell-caption font-normal text-muted-foreground">
              {who}
            </span>
          </span>
        </PopoverTrigger>
        <PopoverContent align="start" className={cn("w-72", popoverMotion)}>
          <p className="m-0 text-[length:var(--type-shell-label-size)] font-medium">
            {companyLabel}
          </p>
          <p className="m-0 mt-1 text-shell-body break-all text-muted-foreground">{tenant}</p>
          {/* Said rather than implied: a token carries one tenant claim, so there is
              nothing here to switch to and somebody looking for a switcher should stop
              looking. */}
          <p className="m-0 mt-3 text-shell-body text-muted-foreground">{oneTenantNote}</p>
        </PopoverContent>
      </Popover>

      {search}

      {/* `relative` on the rule `shell-frame.tsx`'s `main` carries: a container that clips is the
          containing block for what it clips, or an out-of-flow child is laid out against
          the viewport instead and hangs out of the region. */}
      <div className="relative min-h-0 flex-1 overflow-y-auto">{nav}</div>

      {/* The account slot. Signing out belongs at the bottom of the rail with the
          person it signs out, not beside the company name in the header. */}
      <form
        method="post"
        action="/auth/logout"
        onSubmit={clearWorkingDrafts}
        className="border-t border-border pt-3"
      >
        <Button
          type="submit"
          variant="ghost"
          className="w-full justify-start gap-2 px-2 text-muted-foreground"
        >
          <LogOutIcon className="size-4" />
          {signOutLabel}
        </Button>
      </form>
    </div>
  );
}

/**
 * The search affordance: a button that LOOKS like a field.
 *
 * Not an input, because typing into it does nothing — it opens the command palette,
 * which is where the typing happens. An input that redirects your keystrokes somewhere
 * else is the version that feels broken, so this is a button shaped like the thing it
 * opens, with the shortcut printed on it.
 */
export function SearchButton({
  label,
  shortcut,
  onOpen,
}: {
  readonly label: string;
  readonly shortcut: string;
  readonly onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex h-8 w-full items-center gap-2 rounded-lg border border-border bg-card px-2 text-shell-body text-muted-foreground transition-colors hover:bg-accent"
    >
      <SearchIcon className="size-3.5 shrink-0" />
      <span className="min-w-0 flex-1 truncate text-left">{label}</span>
      <kbd className="shrink-0 rounded border border-border bg-muted px-1.5 py-0.5 font-sans text-[10px] text-muted-foreground">
        {shortcut}
      </kbd>
    </button>
  );
}
